import { useMemo, useState } from "react";
import { t } from "@lingui/core/macro";

import { Buildset, getBuildsetUrls } from "_/data/buildsets";
import { useChannels, useSetBuildsetOfChannel, Channel } from "_/data/channels";
import { Permission } from "_/data/rbac";

import { OverlayMenu, MenuItem } from "_/components/overlay-menu";
import { Button } from "_/components/button";
import { Icon } from "_/components/icon";
import { Select, Option } from "_/components/select";
import { SubmitCancelButtons, useModal } from "_/components/modal";
import { useAddToast } from "_/components/toasts";
import { FileDownloadLink } from "_/components/file-download-link";
import { testPermissions } from "_/components/permission-test";
import { formatBuildsetLabel } from "_/utils/buildset-display";

export const ActionsMenu = (props: {
  buildset: Buildset;
  rootPermissions: Permission[];
}) => {
  const { buildset, rootPermissions } = props;
  const addToast = useAddToast();
  const { data: channels = [] } = useChannels();
  const setBuildsetMutation = useSetBuildsetOfChannel();
  const { id, displayName, displayVersion: rawDisplayVersion } = buildset;
  const displayVersion = rawDisplayVersion ?? "";
  const displayNameFormatted = formatBuildsetLabel({
    id,
    displayName,
    displayVersion,
  });
  const { manifestUrl, isoUrl, raucbUrl } = getBuildsetUrls(id, displayVersion);
  const canPromoteChannel = testPermissions(
    ["channels/promote"],
    rootPermissions
  );

  const assignModal = useModal<Buildset>();
  const confirmModal = useModal<void>();
  const AssignModal = assignModal.Modal;
  const ConfirmModal = confirmModal.Modal;

  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);

  // Keep same sorting as ChannelsTable
  const options: Option<Channel>[] = useMemo(
    () =>
      channels
        .toSorted((a, b) => {
          if (b.totalMachines !== a.totalMachines) {
            return b.totalMachines - a.totalMachines;
          }
          return a.name.localeCompare(b.name);
        })
        .map((ch) => ({
          label: ch.name,
          value: ch.name,
          data: ch,
        })),
    [channels]
  );

  const totalMachines = selectedChannel?.totalMachines ?? 0;

  const closeAssign = () => {
    setSelectedChannel(null);
    assignModal.closeModal();
  };

  const handleAssign = async () => {
    if (!selectedChannel) return;
    if (selectedChannel.totalMachines > 0) {
      confirmModal.openModal();
    } else {
      await handleConfirmAssign();
    }
  };

  const handleConfirmAssign = async () => {
    if (!selectedChannel) return;
    const { name } = selectedChannel;

    await setBuildsetMutation.mutateAsync(
      { channelName: name, buildsetId: buildset.id },
      {
        onSuccess: () => {
          addToast({
            title: t`common.success`,
            kind: "success",
            content: t`components.admin-view.assign-channel-success-toast ${displayNameFormatted.value} ${name}`,
          });
          confirmModal.closeModal();
          closeAssign();
        },
        onError: (error) => {
          console.error(error);
          addToast({
            title: t`common.error`,
            kind: "danger",
            content: t`components.admin-view.assign-channel-fail-toast`,
          });
        },
      }
    );
  };

  const items: MenuItem[] = [
    {
      render: t`components.admin-view.assign-to-channel`,
      onClick: () => assignModal.openModal(buildset),
      disabled: !canPromoteChannel,
    },
    {
      render: (
        <FileDownloadLink
          href={isoUrl}
          title={displayNameFormatted.value}
          variant="plain"
        >
          {t`components.admin-view.download-base-image`}
        </FileDownloadLink>
      ),
    },
    {
      render: (
        <FileDownloadLink
          href={raucbUrl}
          title={displayNameFormatted.value}
          variant="plain"
        >
          {t`components.admin-view.download-update-bundle`}
        </FileDownloadLink>
      ),
    },
    {
      render: (
        <a
          href={manifestUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!manifestUrl}
        >
          {t`components.admin-view.view-manifest`}
        </a>
      ),
    },
  ];

  return (
    <>
      <OverlayMenu
        target={
          <Button
            kind="secondary"
            size="small"
            iconOnly
            aria-label={t`common.actions`}
          >
            <Icon variant="MoreVert" />
          </Button>
        }
        placement="bottomStart"
        items={items}
      />

      <AssignModal title={t`components.admin-view.assign-buildset`}>
        <div>
          <Select<Option<Channel>, false>
            options={options}
            value={selectedChannel}
            onChange={(ch: Channel | null) => setSelectedChannel(ch)}
            label={t`common.channel`}
            placeholder={t`components.admin-view.select-channel`}
          />
          <SubmitCancelButtons
            onCancel={closeAssign}
            onSubmit={handleAssign}
            disableSubmit={!selectedChannel || setBuildsetMutation.isPending}
            submitButtonLabel={t`common.assign`}
          />
        </div>
      </AssignModal>

      <ConfirmModal title={t`common.confirm`}>
        <div>
          {t`components.admin-view.confirm-channel-change ${displayVersion} ${totalMachines}`}
          <SubmitCancelButtons
            onCancel={confirmModal.closeModal}
            onSubmit={handleConfirmAssign}
            submitButtonLabel={t`common.assign`}
            disableSubmit={false}
          />
        </div>
      </ConfirmModal>
    </>
  );
};
